import { Box, FormControl, FormErrorMessage, Input } from '@chakra-ui/core';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { templateNameSchema } from '../errorHandlers/formValidation.schema';

const FIELD_KEY = 'name';

const FOCUS_WITHIN_STYLE = {
  borderColor: 'rgba(92, 158, 250, 1)',
  backgroundColor: 'rgba(0, 0, 255, 0.02)',
} as const;

const ERROR_FOCUS_WITHIN_STYLE = {
  borderColor: 'red.300',
  backgroundColor: 'red.50',
} as const;

const BASE_FORM_CONTROL_STYLE = {
  borderRadius: 'md',
  border: '2px solid transparent',
} as const;

const ERROR_FORM_CONTROL_STYLE = {
  borderColor: 'red.300',
  backgroundColor: 'red.50',
} as const;

type ValidationError = {
  message: string;
  context?: Record<string, unknown>;
} | null;

const useValidation = () => {
  const setError = QuestionnaireTemplateStore.useStoreActions((a) => a.setFieldError);
  const clearError = QuestionnaireTemplateStore.useStoreActions((a) => a.clearFieldError);

  const computeError = useCallback((value: string): ValidationError => {
    const { error } = templateNameSchema.validate(value);
    if (!error) return null;
    const detail = error.details[0];
    return {
      message: detail.message,
      context: detail.context as Record<string, unknown> | undefined,
    };
  }, []);

  const validate = useCallback(
    (value: string) => {
      const result = computeError(value);
      if (result) {
        setError({ key: FIELD_KEY, error: result });
        return;
      }
      clearError(FIELD_KEY);
    },
    [computeError, setError, clearError],
  );

  return validate;
};

export const QuestionTemplateNameInput = React.memo(function QuestionTemplateNameInput() {
  const { t } = useTranslation('questionnaireTemplate');

  const templateName = QuestionnaireTemplateStore.useStoreState((s) => s.templateName);
  const fieldError = QuestionnaireTemplateStore.useStoreState((s) => s.errors[FIELD_KEY]);
  const setTemplateName = QuestionnaireTemplateStore.useStoreActions((a) => a.setTemplateName);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const touchedRef = useRef<boolean>(false);

  const validate = useValidation();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      touchedRef.current = true;
      setTemplateName(nextValue);
      validate(nextValue);
    },
    [setTemplateName, validate],
  );

  const onBlur = useCallback(() => {
    touchedRef.current = true;
    validate(templateName);
  }, [templateName, validate]);

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }, []);

  const hasError = Boolean(fieldError);

  const formControlStyle = hasError ? { ...BASE_FORM_CONTROL_STYLE, ...ERROR_FORM_CONTROL_STYLE } : BASE_FORM_CONTROL_STYLE;

  const focusStyle = hasError ? ERROR_FOCUS_WITHIN_STYLE : FOCUS_WITHIN_STYLE;

  const inputHeight = hasError ? '50px' : '72px';

  const errorMessage = useMemo(() => {
    if (!fieldError) return null;
    return t(fieldError.message, fieldError.context);
  }, [fieldError, t]);

  return (
    <Box flex={1}>
      <FormControl isInvalid={hasError} {...formControlStyle} _focusWithin={focusStyle}>
        <Input
          ref={inputRef}
          isInvalid={hasError}
          variant="unstyled"
          placeholder={t('formBuilder.header.templateNameInputPlaceholder')}
          height={inputHeight}
          py={4}
          pl={6}
          pr={2}
          value={templateName}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          data-testid="question-template-name-input"
          _placeholder={{ color: 'gray.400', fontWeight: 'normal' }}
          fontWeight="bold"
        />
        {hasError && (
          <FormErrorMessage mt={0} fontSize="xs" pl={6}>
            {errorMessage}
          </FormErrorMessage>
        )}
      </FormControl>
    </Box>
  );
});
